import Link from 'next/link';
import { components } from 'react-select';
// Constants
import {
  ATHLETES,
  CREATORS,
  STREAMS,
  MERCH,
  PRODUCTS,
  EXPERIENCES,
} from 'constants/routes';
// Types
import { SearchBarGroupProps } from 'components/HomePage/components/V3/SearchBar/SearchBar';
import { useSearchBarContext } from 'components/HomePage/components/V3/SearchBar/hook/useSearchBarContext';
// Helpers
import { getShopTabs, ShopTab } from 'helpers/shopTabs';
// Ui3
import Button from 'ui3/Button/Button';
// Styles
import styles from './SearchGroupHeading.module.scss';

interface SearchGroupHeadingProps {
  data: SearchBarGroupProps;
  children: React.ReactNode;
  cx: (...args: (string | undefined)[]) => string;
  getStyles: (
    name: string,
    props: SearchGroupHeadingProps
  ) => React.CSSProperties;
  className?: string;
  id: string;
  selectProps: Record<string, unknown>;
}

const getViewAllLink = (
  groupLabel: string,
  shopTab: ShopTab,
  searchTerm: string
): string | null => {
  const encodedSearch = searchTerm
    ? `?searchTerm=${encodeURIComponent(searchTerm)}`
    : '';

  // For Shop/Latest Drops, use shopTab to determine link
  if (groupLabel === 'Shop' || groupLabel === 'Latest Drops') {
    switch (shopTab) {
      case 'merch':
        return `${MERCH}${encodedSearch}`;
      case 'memorabilia':
        return `${PRODUCTS}${encodedSearch}`;
      case 'experiences':
        return `${EXPERIENCES}${encodedSearch}`;
    }
  }

  switch (groupLabel) {
    case 'Top Athletes':
    case 'Athletes':
      return `${ATHLETES}${encodedSearch}`;
    case 'Top Creators':
    case 'Creators':
      return `${CREATORS}${encodedSearch}`;
    case 'Latest Streams':
    case 'Watch':
      return `${STREAMS}${encodedSearch}`;
    default:
      return null;
  }
};

const getViewAllText = (groupLabel: string, shopTab: ShopTab): string => {
  if (groupLabel === 'Shop' || groupLabel === 'Latest Drops') {
    switch (shopTab) {
      case 'merch':
        return 'View Merch';
      case 'memorabilia':
        return 'View Memorabilia';
      case 'experiences':
        return 'View Experiences';
    }
  }

  switch (groupLabel) {
    case 'Latest Streams':
    case 'Watch':
      return 'Go to Watch';
    default:
      return 'View All';
  }
};

const SearchGroupHeading = (props: SearchGroupHeadingProps) => {
  const { data } = props;
  const {
    activeShopTab,
    setShopTab,
    searchValue,
    availableCategories,
  } = useSearchBarContext();
  const isShopSection = data.label === 'Shop' || data.label === 'Latest Drops';
  const viewAllLink = getViewAllLink(data.label, activeShopTab, searchValue);
  const viewAllText = getViewAllText(data.label, activeShopTab);

  const shopTabs = isShopSection ? getShopTabs(availableCategories) : [];

  return (
    <components.GroupHeading {...props}>
      <div className={styles.groupHeading}>
        <span className={styles.label}>{data.label}</span>
        {viewAllLink && (
          <Link href={viewAllLink}>
            <a className={styles.viewAllLink}>{viewAllText}</a>
          </Link>
        )}
      </div>
      {isShopSection && shopTabs.length > 0 && (
        <div className={styles.shopTabs}>
          {shopTabs.map((tab) => (
            <Button
              key={tab.key}
              variant="secondary"
              subVariant="text"
              size="small"
              icon={tab.icon}
              iconPosition="left"
              className={`${styles.shopTab} ${
                activeShopTab === tab.key ? styles.active : ''
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShopTab(tab.key);
              }}
            >
              {tab.label}
            </Button>
          ))}
        </div>
      )}
    </components.GroupHeading>
  );
};

export default SearchGroupHeading;
